"use client";

import {
    Badge,
    Combobox,
    type ComboboxRootProps,
    createListCollection,
    HStack,
    Portal,
    Span,
    useFilter,
} from "@chakra-ui/react";
import type React from "react";
import {
    type ElementType,
    Fragment,
    type ReactNode,
    useEffect,
    useMemo,
    useState,
} from "react";

export interface ComboboxItem {
    label: string;
    value: string;
    icon?: ReactNode;
    imagePath?: string;
}

export interface ComboboxSelectProps<T> {
    items: T[];
    value: string[];
    onChange: (selected: string[]) => void;
    onOpen?: () => void;
    onInputValueChange?: (value: string) => void;
    label?: string;
    placeholder?: string;
    width?: string;
    portalled?: boolean;
    sensitivity?: Intl.CollatorOptions["sensitivity"];
    variant?: ComboboxRootProps["variant"];
    placement?: ComboboxRootProps["placeContent"];
    comboBoxProps?: Omit<Combobox.ControlProps, "children">;
    comboInputProps?: Omit<Combobox.InputProps, "children">;
    comboRootProps?: Omit<
        Combobox.RootProps,
        | "children"
        | "collection"
        | "onInputValueChange"
        | "value"
        | "onValueChange"
    >;
    caretAs?: ElementType;
    multiple?: boolean;
    itemToString?: (item: T) => string;
    itemToValue?: (item: T) => string;
    renderValue?: (item: T) => React.ReactNode;
    renderOption?: (item: T) => React.ReactNode;
    cleanable?: boolean;
}

const ComboboxSelect: React.FC<ComboboxSelectProps<ComboboxItem>> = ({
    items,
    value,
    onChange,
    onOpen,
    onInputValueChange,
    placeholder = "Type to search",
    label,
    width = "320px",
    sensitivity = "base",
    portalled = true,
    comboBoxProps,
    comboInputProps,
    comboRootProps,
    caretAs: CaretIcon,
    variant = "outline",
    placement = "bottom",
    renderValue,
    renderOption,
    itemToString = (item: ComboboxItem) => item.label,
    itemToValue = (item: ComboboxItem) => item.value,
    cleanable = true,
    multiple = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [localValue, setLocalValue] = useState<string[]>(value);
    const [filterText, setFilterText] = useState("");
    const { contains } = useFilter({ sensitivity });

    // Built directly from `items` on every render (rather than mirroring
    // `items` into separate state via an effect) so the collection given to
    // Combobox.Root is never a render behind the `items` prop - otherwise
    // Ark's Combobox never re-syncs its displayed value for a selection
    // that resolves after the collection was empty at mount.
    const collection = useMemo(() => {
        const filteredItems = filterText
            ? items.filter(item => contains(itemToString(item), filterText))
            : items;
        return createListCollection({
            items: filteredItems,
            itemToString,
            itemToValue,
        });
    }, [items, filterText, contains, itemToString, itemToValue]);

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    const ContentWrapper = portalled ? Portal : Fragment;
    const selectedItem = items.find(item => item.value === localValue[0]);
    const selectedItems = items.filter(item => localValue.includes(item.value));

    return (
        <Combobox.Root
            key={`${localValue[0] ?? "none"}:${selectedItem ? "resolved" : "pending"}`}
            collection={collection}
            onInputValueChange={e => {
                setFilterText(e.inputValue);
                onInputValueChange?.(e.inputValue);
            }}
            value={localValue}
            onValueChange={e => {
                onChange(e.value ?? []);
                if (!multiple) {
                    setIsOpen(false);
                }
            }}
            width={width}
            open={isOpen}
            onOpenChange={e => {
                setIsOpen(e.open);
                if (e.open) {
                    onOpen?.();
                } else {
                    setFilterText("");
                }
            }}
            openOnClick
            variant={variant}
            placeContent={placement}
            multiple={multiple}
            {...comboRootProps}
        >
            {label && <Combobox.Label>{label}</Combobox.Label>}
            <Combobox.Control
                height={multiple ? "auto" : "34px"}
                minHeight={multiple ? "34px" : "unset"}
                display="flex"
                flexDirection={multiple ? "column" : "row"}
                position="relative"
                borderWidth="1px"
                borderColor="border"
                borderRadius="md"
                padding={multiple ? "4px 4px 4px 4px" : undefined}
                gap={multiple ? "4px" : undefined}
                {...comboBoxProps}
            >
                {/* Content wrapper: badges row + input row for multi-select */}
                <Span
                    display="flex"
                    flexDirection={multiple ? "column" : "row"}
                    width="100%"
                    gap={multiple ? "4px" : undefined}
                >
                    {multiple && selectedItems.length > 0 && (
                        <HStack
                            flexWrap="wrap"
                            gap="4px"
                            width="100%"
                            paddingRight="40px"
                        >
                            {selectedItems.map(item => (
                                <Badge
                                    key={item.value}
                                    colorPalette="blue"
                                    size="sm"
                                    variant="subtle"
                                    display="flex"
                                    alignItems="center"
                                    gap="4px"
                                    px={2}
                                    py={0.5}
                                    flexShrink={0}
                                >
                                    <Span
                                        maxWidth="150px"
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        whiteSpace="nowrap"
                                    >
                                        {itemToString(item)}
                                    </Span>
                                    <Span
                                        cursor="pointer"
                                        onClick={e => {
                                            e.stopPropagation();
                                            const newValue = localValue.filter(
                                                v => v !== item.value,
                                            );
                                            onChange(newValue);
                                        }}
                                        fontSize="10px"
                                        lineHeight="1"
                                    >
                                        ×
                                    </Span>
                                </Badge>
                            ))}
                        </HStack>
                    )}
                    {!multiple && renderValue && selectedItem && !isOpen && (
                        <div
                            style={{
                                position: "absolute",
                                top: "50%",
                                transform: "translateY(-50%)",
                                left: "12px",
                                display: "flex",
                                alignItems: "center",
                                pointerEvents: "none",
                                zIndex: 1,
                                background: "inherit",
                            }}
                        >
                            {renderValue(selectedItem)}
                        </div>
                    )}
                    {/* Input row */}
                    <Span
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        width="100%"
                        flex={multiple ? undefined : 1}
                        minHeight={multiple ? "28px" : "unset"}
                    >
                        <Combobox.Input
                            placeholder={
                                !multiple && selectedItem && renderValue
                                    ? ""
                                    : placeholder
                            }
                            opacity={
                                !multiple &&
                                selectedItem &&
                                renderValue &&
                                !isOpen
                                    ? 0
                                    : 1
                            }
                            onKeyDown={e => {
                                comboInputProps?.onKeyDown?.(e);
                                if (!multiple && e.key === "Enter") {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    const filteredItems = collection.items;
                                    if (
                                        localValue.length === 0 &&
                                        filteredItems.length > 0
                                    ) {
                                        const firstItem = filteredItems[0];
                                        onChange([firstItem.value]);
                                    }
                                    setIsOpen(false);
                                }
                            }}
                            height={multiple ? "28px" : "100%"}
                            minHeight="unset"
                            flex={1}
                            {...comboInputProps}
                        />
                    </Span>
                </Span>
                <Combobox.IndicatorGroup
                    height={multiple ? "28px" : "100%"}
                    display="flex"
                    alignItems="center"
                    position={multiple ? "absolute" : undefined}
                    top={multiple ? "4px" : undefined}
                    right={multiple ? "4px" : undefined}
                >
                    {cleanable && <Combobox.ClearTrigger />}
                    <Combobox.Trigger
                        height={multiple ? "28px" : "100%"}
                        display="flex"
                        alignItems="center"
                    >
                        {CaretIcon && <CaretIcon />}
                    </Combobox.Trigger>
                </Combobox.IndicatorGroup>
            </Combobox.Control>
            <ContentWrapper>
                <Combobox.Positioner>
                    <Combobox.Content>
                        <Combobox.Empty>No items found</Combobox.Empty>
                        {collection.items.map(item => (
                            <Combobox.Item item={item} key={item.value}>
                                {/* Use renderOption if provided, otherwise fallback to label string */}
                                {renderOption ? (
                                    renderOption(item)
                                ) : (
                                    <HStack width="100%">
                                        {item.icon && <Span>{item.icon}</Span>}
                                        <Span flex="1">
                                            {itemToString?.(item)}
                                        </Span>
                                    </HStack>
                                )}
                                <Combobox.ItemIndicator />
                            </Combobox.Item>
                        ))}
                    </Combobox.Content>
                </Combobox.Positioner>
            </ContentWrapper>
        </Combobox.Root>
    );
};

export default ComboboxSelect;
